import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Container,
  Heading,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useRef, useState } from "react";
import {
  createAdminBookClub,
  deleteAdminBookClub,
  getAdminBookClub,
  updateAdminBookClub,
} from "../../../services/communityApi";
import Spinner from "../../../shared/components/Spinner";
import BookClubAdminForm from "../components/BookClubAdminForm";

const BookClubAdminEdit = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const cancelRef = useRef();
  const isEditing = Boolean(clubId);
  const clubQuery = useQuery(
    ["adminCommunityBookClub", clubId],
    () => getAdminBookClub(clubId),
    { enabled: isEditing }
  );
  const saveClub = useMutation(
    ({ values }) =>
      isEditing
        ? updateAdminBookClub(clubId, values)
        : createAdminBookClub(values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("adminCommunityBookClubs");
        toast({ title: "Book club saved.", status: "success" });
        navigate("/bookclub/admin");
      },
      onError: (error) => {
        toast({
          title: "Book club could not be saved.",
          description:
            error.response?.data?.message || "Check the form and try again.",
          status: "error",
        });
      },
    }
  );
  const deleteClub = useMutation(() => deleteAdminBookClub(clubId), {
    onSuccess: () => {
      setIsDeleteOpen(false);
      queryClient.invalidateQueries("adminCommunityBookClubs");
      toast({ title: "Draft book club deleted.", status: "success" });
      navigate("/bookclub/admin");
    },
    onError: (error) => {
      toast({
        title: "Draft book club could not be deleted.",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
      });
    },
  });

  if (isEditing && clubQuery.isLoading) {
    return <Spinner />;
  }

  if (isEditing && (clubQuery.isError || !clubQuery.data?.data)) {
    return (
      <Container py="8">
        <Text>Unable to load this book club.</Text>
        <Button mt="4" onClick={() => navigate("/bookclub/admin")}>
          Back to book clubs
        </Button>
      </Container>
    );
  }

  const club = isEditing ? clubQuery.data.data : undefined;

  return (
    <Container maxW="container.xl" py="8">
      <Button
        variant="ghost"
        mb="4"
        onClick={() => navigate("/bookclub/admin")}
      >
        ← Book clubs
      </Button>
      <Box mb="6">
        <Heading>{isEditing ? "Edit book club" : "New book club"}</Heading>
        <Text mt="2" color="gray.400">
          Build the club, its reading order, and its opening discussion prompts
          in one place.
        </Text>
      </Box>
      <BookClubAdminForm
        club={club}
        onSubmit={(values) => saveClub.mutate({ values })}
        isSaving={saveClub.isLoading}
      />
      {isEditing && club && !club.published ? (
        <>
          <Box mt="6" pt="6" borderTopWidth="1px" borderColor="gray.600">
            <Button
              colorScheme="red"
              variant="outline"
              onClick={() => setIsDeleteOpen(true)}
            >
              Delete draft
            </Button>
          </Box>
          <AlertDialog
            isOpen={isDeleteOpen}
            leastDestructiveRef={cancelRef}
            onClose={() => {
              if (!deleteClub.isLoading) {
                setIsDeleteOpen(false);
              }
            }}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader>Delete draft book club?</AlertDialogHeader>
                <AlertDialogBody>
                  This will remove this draft book club from the admin list.
                  Published book clubs cannot be deleted.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button
                    ref={cancelRef}
                    onClick={() => setIsDeleteOpen(false)}
                    isDisabled={deleteClub.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    ml="3"
                    onClick={() => deleteClub.mutate()}
                    isLoading={deleteClub.isLoading}
                    loadingText="Deleting"
                  >
                    Delete draft
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      ) : null}
    </Container>
  );
};

export default BookClubAdminEdit;
